import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Select } from "@/components/ui/Select";
import { getContractorDisplayName, type ContractorLike } from "../utils";
import {
  EFFECT_OPTIONS,
  SCOPE_OPTIONS,
  type ContractorKnowledgeNote,
  type KnowledgeEffect,
  type KnowledgeNotePayload,
  type KnowledgeScope,
} from "../api/knowledgeNotes";

/**
 * Formularz jednej zasady podpowiadania — nowej albo edytowanej. Podwykonawcę
 * wybiera się tylko w rejestrze zbiorczym (na karcie firmy jest ustalony), przedsięwzięcie
 * opcjonalnie wszędzie.
 */

interface ContractorRow extends ContractorLike {
  id: string;
}
interface ProjectRow {
  id: string;
  name: string;
  short_name: string;
  is_active: boolean;
}

interface Props {
  /** Zasada do edycji; brak = nowa. */
  initial?: ContractorKnowledgeNote;
  /** Ustalona firma (karta podwykonawcy) — ukrywa picker firmy. */
  fixedContractorId?: string;
  onSubmit: (payload: KnowledgeNotePayload) => void;
  onCancel?: () => void;
  pending?: boolean;
  error?: string | null;
  submitLabel?: string;
}

export function KnowledgeNoteForm({
  initial,
  fixedContractorId,
  onSubmit,
  onCancel,
  pending,
  error,
  submitLabel,
}: Props) {
  const [body, setBody] = useState(initial?.body ?? "");
  const [scope, setScope] = useState<KnowledgeScope>(initial?.scope ?? "");
  const [effect, setEffect] = useState<KnowledgeEffect>(initial?.effect ?? "advise");
  const [validFrom, setValidFrom] = useState(initial?.valid_from ?? "");
  const [validUntil, setValidUntil] = useState(initial?.valid_until ?? "");
  const [contractor, setContractor] = useState(fixedContractorId ?? initial?.contractor ?? "");
  const [project, setProject] = useState(initial?.project ?? "");

  const contractorsQ = useQuery({
    queryKey: ["contractors", "active"],
    queryFn: () =>
      apiClient
        .get<{ data: ContractorRow[] }>("/contractors/", { params: { is_active: "true" } })
        .then((r) => r.data.data),
    enabled: !fixedContractorId,
  });
  const projectsQ = useQuery({
    queryKey: ["projects"],
    queryFn: () => apiClient.get<{ data: ProjectRow[] }>("/projects/").then((r) => r.data.data),
  });

  const contractorOptions = useMemo(
    () =>
      (contractorsQ.data ?? [])
        .map((c) => ({ value: c.id, label: getContractorDisplayName(c) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [contractorsQ.data],
  );
  const projectOptions = useMemo(
    () =>
      (projectsQ.data ?? [])
        .filter((p) => p.is_active || p.id === project)
        .map((p) => ({ value: p.id, label: `${p.short_name} — ${p.name}` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [projectsQ.data, project],
  );

  const windowInvalid = !!validFrom && !!validUntil && validFrom > validUntil;
  const canSubmit = !!body.trim() && !windowInvalid && !pending;
  const effectHint = EFFECT_OPTIONS.find((o) => o.value === effect)?.hint;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      body: body.trim(),
      scope,
      effect,
      valid_from: validFrom || null,
      valid_until: validUntil || null,
      contractor: fixedContractorId ?? (contractor || null),
      project: project || null,
    });
  };

  return (
    <div className="space-y-2" data-testid="knowledge-note-form">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        placeholder="Treść zasady, np. „Podpowiadaj tę firmę przy WPW tylko, jeśli w ciągu ostatnich 8 h dostała zlecenie MDCP na tym samym projekcie”"
        className="w-full border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5 text-sm text-[var(--color-text-primary)]"
      />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Select
          label="Rodzaj zlecenia"
          options={SCOPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={scope}
          onChange={(e) => setScope(e.target.value as KnowledgeScope)}
        />
        <Select
          label="Działanie"
          options={EFFECT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={effect}
          onChange={(e) => setEffect(e.target.value as KnowledgeEffect)}
        />
      </div>
      {effectHint && (
        <p className="text-[11px] leading-snug text-[var(--color-text-muted)]">{effectHint}</p>
      )}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
            Obowiązuje od
          </span>
          <DatePicker value={validFrom} onChange={setValidFrom} placeholder="bez ograniczenia" />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
            Obowiązuje do
          </span>
          <DatePicker value={validUntil} onChange={setValidUntil} placeholder="bez ograniczenia" />
        </div>
      </div>
      {windowInvalid && (
        <p className="text-xs text-red-600">Data „do” nie może być wcześniejsza niż „od”.</p>
      )}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {!fixedContractorId && (
          <SearchableSelect
            label="Podwykonawca"
            placeholder="— zasada globalna (każda firma) —"
            options={contractorOptions}
            value={contractor}
            onChange={(e) => setContractor(e.target.value)}
          />
        )}
        <SearchableSelect
          label="Przedsięwzięcie"
          placeholder="— każde przedsięwzięcie —"
          options={projectOptions}
          value={project}
          onChange={(e) => setProject(e.target.value)}
        />
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={submit} disabled={!canSubmit} loading={pending}>
          {submitLabel ?? (initial ? "Zapisz" : "Dodaj zasadę")}
        </Button>
        {onCancel && (
          <button
            type="button"
            className="text-xs text-[var(--color-text-secondary)] underline"
            onClick={onCancel}
          >
            Anuluj
          </button>
        )}
        {error && <span className="text-xs text-red-500">{error}</span>}
      </div>
    </div>
  );
}
